import type { Query, QueryKey, UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { tasksApi } from '../api';
import type { TaskSnapshot } from '../api/tasks';
import { useAuthenticatedRequest } from '../config/auth-config';
import { isActiveStatus } from '../pages/tasks/task-utils';

export const TASKS_QUERY_KEY: QueryKey = ['tasks'];

type UseTasksOptions = {
  driver?: boolean;
  enabled?: boolean;
  activePollMs?: number;
  idleMinPollMs?: number;
  idleMaxPollMs?: number;
  backoffFactor?: number;
};

const hasActiveTasks = (tasks?: TaskSnapshot[]) => tasks?.some(task => isActiveStatus(task.status)) ?? false;

export const useTasks = ({
  driver = false,
  enabled = true,
  activePollMs = 3_000,
  idleMinPollMs = 15_000,
  idleMaxPollMs = 60_000,
  backoffFactor = 2,
}: UseTasksOptions = {}): UseQueryResult<TaskSnapshot[], Error> => {
  const authRequest = useAuthenticatedRequest();
  const idleStreakRef = useRef(0);
  const lastIdsRef = useRef<string[]>([]);

  return useQuery<TaskSnapshot[], Error>({
    queryKey: TASKS_QUERY_KEY,
    queryFn: ({ signal }) => authRequest(config => tasksApi.listTasks({ ...config, signal })).then(r => r.data ?? []),
    gcTime: 5 * 60 * 1000,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchInterval:
      driver && enabled
        ? (query: Query<TaskSnapshot[], Error, TaskSnapshot[], QueryKey>) => {
            const data = query.state.data;
            const active = hasActiveTasks(data);

            const currentIds = (data ?? []).map(t => t.id).sort();
            const lastIds = lastIdsRef.current;
            const newIdsAppeared =
              currentIds.length > lastIds.length || currentIds.some((id, index) => id !== lastIds[index]);
            lastIdsRef.current = currentIds;

            if (active || newIdsAppeared) {
              idleStreakRef.current = 0;
              return activePollMs;
            }

            const next = Math.min(idleMinPollMs * Math.pow(backoffFactor, idleStreakRef.current), idleMaxPollMs);
            idleStreakRef.current += 1;
            return next;
          }
        : false,
    refetchIntervalInBackground: driver && enabled,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1_000 * Math.pow(2, attemptIndex), 8_000),
    select: (tasks: TaskSnapshot[]) => [...tasks].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? '')),
  });
};
